/**
 * BatchProfiler — Agregación de métricas por fase + concurrencia controlada
 *
 * Recoge las mediciones individuales de N items (PerformanceProfiler)
 * y genera estadísticas agregadas (p50, p95, mean, max) por etapa.
 * Incluye utilidad de ejecución concurrente con control de paralelismo.
 *
 * @module services/performance/BatchProfiler
 * @version 1.0.0 — F6.04
 */

import {
  PerformanceProfiler,
  type StageMeasurement as _StageMeasurement,
  type ProfilingReport,
} from './PerformanceProfiler';

// =============================================================================
// TIPOS
// =============================================================================

/** Estadísticas agregadas de una etapa */
export interface StageAggregate {
  stage: string;
  count: number;
  totalMs: number;
  meanMs: number;
  minMs: number;
  maxMs: number;
  p50Ms: number;
  p95Ms: number;
}

/** Informe agregado de un lote completo */
export interface BatchProfilingReport {
  /** Número total de ítems procesados */
  itemCount: number;
  /** Tiempo total del lote (ms) */
  totalMs: number;
  /** Throughput: ítems/segundo */
  throughput: number;
  /** Estadísticas por etapa */
  stageAggregates: StageAggregate[];
  /** Top bottleneck (etapa con mayor p95) */
  topBottleneck: StageAggregate | null;
  /** ¿Cumple DoD (media ≤ 30 s/ítem)? */
  meetsDoD: boolean;
  /** Informes individuales */
  itemReports: ProfilingReport[];
  /** Resumen legible */
  summary: string;
}

// =============================================================================
// CONSTANTES
// =============================================================================

const DOD_MEAN_MS = 30_000;

// =============================================================================
// UTILIDADES
// =============================================================================

/** Calcula percentil de un array ya ordenado (0-100) */
function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  if (sorted.length === 1) return sorted[0];
  const idx = (p / 100) * (sorted.length - 1);
  const lower = Math.floor(idx);
  const upper = Math.ceil(idx);
  if (lower === upper) return sorted[lower];
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (idx - lower);
}

// =============================================================================
// BATCH PROFILER
// =============================================================================

export class BatchProfiler {
  private readonly reports: ProfilingReport[] = [];
  private startedAt = 0;

  /** Inicia la medición del lote */
  start(): void {
    this.startedAt = performance.now();
    this.reports.length = 0;
  }

  /** Crea un profiler individual para un ítem del lote */
  createItemProfiler(itemId: string): PerformanceProfiler {
    return new PerformanceProfiler(itemId);
  }

  /** Registra el informe de un ítem ya procesado */
  addReport(report: ProfilingReport): void {
    this.reports.push(report);
  }

  /** Genera informe agregado del lote */
  aggregate(): BatchProfilingReport {
    const totalMs = performance.now() - this.startedAt;
    const itemCount = this.reports.length;

    // Recoger todas las mediciones por etapa
    const byStage = new Map<string, number[]>();
    for (const report of this.reports) {
      for (const stage of report.stages) {
        const arr = byStage.get(stage.stage) ?? [];
        arr.push(stage.durationMs);
        byStage.set(stage.stage, arr);
      }
    }

    // Calcular agregados
    const stageAggregates: StageAggregate[] = [];
    for (const [stage, durations] of byStage) {
      const sorted = [...durations].sort((a, b) => a - b);
      const totalStageMs = sorted.reduce((s, v) => s + v, 0);
      stageAggregates.push({
        stage,
        count: sorted.length,
        totalMs: totalStageMs,
        meanMs: sorted.length > 0 ? totalStageMs / sorted.length : 0,
        minMs: sorted[0] ?? 0,
        maxMs: sorted[sorted.length - 1] ?? 0,
        p50Ms: percentile(sorted, 50),
        p95Ms: percentile(sorted, 95),
      });
    }

    // Ordenar por p95 desc para encontrar top bottleneck
    stageAggregates.sort((a, b) => b.p95Ms - a.p95Ms);
    const topBottleneck = stageAggregates[0] ?? null;

    const throughput = totalMs > 0 ? (itemCount / totalMs) * 1000 : 0;
    const meanPerItem = itemCount > 0 ? totalMs / itemCount : 0;
    const meetsDoD = meanPerItem <= DOD_MEAN_MS;

    const stageLines = stageAggregates
      .map(
        (s) =>
          `  ${s.stage}: mean=${s.meanMs.toFixed(1)}ms p50=${s.p50Ms.toFixed(1)}ms ` +
          `p95=${s.p95Ms.toFixed(1)}ms max=${s.maxMs.toFixed(1)}ms (n=${s.count})`,
      )
      .join('\n');

    const summary = [
      `=== Batch Profiling (${itemCount} ítems) ===`,
      `Total: ${totalMs.toFixed(0)}ms | Throughput: ${throughput.toFixed(2)} ítems/s`,
      `Media/ítem: ${meanPerItem.toFixed(0)}ms ${meetsDoD ? '✓ DoD OK' : '✗ Excede DoD'}`,
      `Top bottleneck: ${topBottleneck ? `${topBottleneck.stage} (p95=${topBottleneck.p95Ms.toFixed(1)}ms)` : 'N/A'}`,
      `Etapas:\n${stageLines}`,
    ].join('\n');

    return {
      itemCount,
      totalMs,
      throughput,
      stageAggregates,
      topBottleneck,
      meetsDoD,
      itemReports: [...this.reports],
      summary,
    };
  }
}

// =============================================================================
// CONCURRENCIA CONTROLADA
// =============================================================================

/**
 * Ejecuta N tareas con concurrencia máxima controlada.
 * Ideal para procesamiento batch que no debe saturar APIs o memoria.
 *
 * @param items - Ítems a procesar
 * @param concurrency - Máximo de tareas simultáneas (default: 3)
 * @param fn - Función que procesa cada ítem
 * @param onItemDone - Callback opcional tras completar cada ítem
 * @returns Resultados en el mismo orden que los inputs
 */
export async function runWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  fn: (item: T, index: number) => Promise<R>,
  onItemDone?: (index: number, total: number) => void,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  async function worker(): Promise<void> {
    while (nextIndex < items.length) {
      const idx = nextIndex++;
      results[idx] = await fn(items[idx], idx);
      onItemDone?.(idx, items.length);
    }
  }

  const workerCount = Math.min(concurrency, items.length);
  const workers = Array.from({ length: workerCount }, () => worker());
  await Promise.all(workers);

  return results;
}
